/*
 * Copyright © 2019 Franck Borel, Martin Helfer, Sebastian Schüpbach
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */

import { Action } from '@ngrx/store';
import { SortOrder } from '../../shared/models/settings.model';
import { AutoCompleteValue, EditDocField } from '../../shared/models/doc-details.model';
import { QueryFormat } from '../../shared/models/query-format';
import { FacetResponseMap, SearchResponse } from '../../shared/models/response-format';
import { DetailedResult } from '../models/detailed-result.model';
import { Result } from '../models/result.model';

/***
 * @ignore
 */
/* eslint-disable @typescript-eslint/no-shadow */
export enum QueryActionTypes {
  MakeSearchRequest = '[Query] Make search request',
  MakeGeoSearchRequest = '[Query] Make geo search request',
  MakeDetailedSearchRequest = '[Query] Make detailed search request',
  MakeDetailedEditRequest = '[Query] Make detailed edit request',
  MakeDetailedNewRequest = '[Query] Make detailed create request',
  MakeSaveDetailedEditRequest = '[Query] Make save detailed edit request',
  MakeSuggestEditFieldSearch = '[Query] Make detailed edit field suggestion request',
  MakeNextDetailedSearchRequest = '[Query] Execute following detailed search request',
  MakePreviousDetailedSearchRequest = '[Query] Execute preceding detailed search request',
  MakeBasketSearchRequest = '[Query] Make basket request',
  SetOffset = '[Query] Set offset of result view',
  SetGeoClusterPrecision = '[Query] Set query geo cluster precision',
  SetRows = '[Query] Set number of results per page',
  SetSortField = '[Query] Set sort field',
  ResultListDisplayMode = '[Query] Set display mode of result list',
  SetSortOrder = '[Query] Set sort order',
  SearchSuccess = '[Query] Search successful',
  SearchFailure = '[Query] Search failed',
  DetailedSearchSuccess = '[Query] Detailed search successful',
  DetailedSaveSuccess = '[Query] Detailed save successful',
  DetailedNewSuccess = '[Query] Detailed new successful',
  MakeSuggestEditFieldSearchSuccess = '[Query] Detailed edit field suggestion request succeeded',
  MakeSuggestEditFieldSearchFailure = '[Query] Detailed edit field suggestion request failed',
  DetailedSearchFailure = '[Query] Detailed search failed',
  DetailedSaveFailure = '[Query] Detailed save failed',
  BasketSearchSuccess = '[Query] Basket search successful',
  BasketSearchFailure = '[Query] Basket search failed',
  NextPage = '[Query] Load next page',
  NextPageSuccess = '[Query] Next page loading succeeded',
  SimpleSearch = '[Query] Exec simple default search',
  SuggestSearchWordSearch = '[Query] Exec auto-complete search search',
  SuggestSearchWordSuccess = '[Query] Exec auto-complete search search success',
  SuggestSearchWordFailure = '[Query] Exec auto-complete search search failure',
  Reset = '[Query] Reset query settings except rows/sort field/order',
  MakeInFacetSearchRequest = '[Query] Make in-facet search request',
  InFacetSearch = '[Query] Exec search in facet',
  InFacetSearchSuccess = '[Query] in-facet search request success',
  ResetQuery = '[Query] Reset query params rows/sort field/order',
}
/* eslint-enable @typescript-eslint/no-shadow */

/**
 * Initializes a new search request
 */
export class MakeSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeSearchRequest;

  /**
   * Contains all set search parameters
   *
   * @param payload {QueryFormat}
   */
  constructor(public payload: QueryFormat) {}
}

/**
 * Initializes a new search request
 */
export class MakeGeoSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeGeoSearchRequest;

  /**
   * Contains all set search parameters
   *
   * @param payload {QueryFormat}
   */
  constructor(public payload: QueryFormat) {}
}

/**
 * Initializes a new request for a detailed result
 */
export class MakeDetailedSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeDetailedSearchRequest;

  /**
   * Contains id of result
   *
   * @param payload
   */
  constructor(public payload: { id: string; fullRecord?: boolean }) {}
}

/**
 * Initializes a new request for a detailed result which follows the doc with the given id.
 */
export class MakeNextDetailedSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeNextDetailedSearchRequest;

  /**
   * Contains cursor of result
   *
   * @param payload {any}
   */
  constructor(public payload: any) {}
}

/**
 * Initializes a new request for a detailed edit result which follows the doc with the given id.
 */
export class MakeDetailedEditRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeDetailedEditRequest;

  /**
   * Contains cursor of result
   *
   * @param payload
   */
  constructor(public payload: { id: string; fullRecord: boolean }) {}
}

/**
 * Initializes a new request to save edited detailed data.
 */
export class MakeSaveDetailedEditRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeSaveDetailedEditRequest;

  /**
   * Contains edited document data
   *
   * @param payload
   */
  constructor(public payload: { object_id: string; fields: EditDocField[]; objectType: string }) {}
}

/**
 * Initializes a new request to create detailed data.
 */
export class MakeDetailedNewRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeDetailedNewRequest;

  /**
   * Contains edited document data
   *
   * @param payload {any}
   */
  constructor(public payload: { doctype: string }) {}
}

/**
 * Perform an edit field suggestion search.
 */
export class MakeSuggestEditFieldSearch implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeSuggestEditFieldSearch;

  /**
   * Contains extra request params
   *
   * @param payload the extra request params
   */
  constructor(public payload: { id: string; field: string; query: string; selected: AutoCompleteValue[] }) {}
}

/**
 * Issued when edit field suggestion search is successful
 */
export class MakeSuggestEditFieldSearchSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeSuggestEditFieldSearchSuccess;

  /**
   * Contains result set
   *
   * @param payload {AutoCompleteValue[]}
   */
  constructor(public payload: AutoCompleteValue[]) {}
}

/**
 * Issued when edit field suggestion search has failed
 */
export class MakeSuggestEditFieldSearchFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeSuggestEditFieldSearchFailure;

  /**
   * Contains error message
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Initializes a new request for a detailed result which precedes the doc with the given id.
 */
export class MakePreviousDetailedSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakePreviousDetailedSearchRequest;

  /**
   * Contains cursor of result
   *
   * @param payload {any}
   */
  constructor(public payload: any) {}
}

/**
 * Initializes a new request request for fetching information of records
 * in a basket
 */
export class MakeBasketSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeBasketSearchRequest;

  /**
   * Contains information of the basket and a list of the ids of the records
   * belonging to it
   *
   * @param payload {any}
   */
  constructor(public payload: any) {}
}

/**
 * Initializes a new in-facet search request
 */
export class MakeInFacetSearchRequest implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.MakeInFacetSearchRequest;

  /**
   * Contains all set search parameters
   *
   * @param payload {QueryFormat}
   */
  constructor(public payload: QueryFormat) {}
}

/**
 * Sets a new offset in the result list
 */
export class SetOffset implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SetOffset;

  /**
   * Contains new offset
   *
   * @param payload {number}
   */
  constructor(public payload: number) {}
}

/**
 * Sets a new offset in the result list
 */
export class SetGeoClusterPrecision implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SetGeoClusterPrecision;

  /**
   * Contains new geo cluster precision
   *
   * @param payload {number}
   */
  constructor(public payload: number) {}
}

/**
 * Sets number of rows on one page in the result list
 */
export class SetRows implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SetRows;

  /**
   * Contains new number of rows
   *
   * @param payload {number}
   */
  constructor(public payload: number) {}
}

/**
 * Sets order by which the result list is sorted
 */
export class SetSortOrder implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SetSortOrder;

  /**
   * Contains new sort order
   *
   * @param payload {SortOrder}
   */
  constructor(public payload: SortOrder) {}
}

/**
 * Sets new field by which the result list is sorted
 */
export class SetSortField implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SetSortField;

  /**
   * Contains name of field
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Sets result list's display mode. Possbile values are
 * defined in the environment (section "documentViewer").
 */
export class SetResultListDisplayMode implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.ResultListDisplayMode;

  /**
   * Contains new sort order
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Issued when search is successful
 *
 * @see MakeSearchRequest
 */
export class SearchSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SearchSuccess;

  /**
   * Contains result set
   *
   * @param payload {SearchResponse}
   */
  constructor(public payload: SearchResponse) {}
}

/**
 * Issued when search has failed
 *
 * @see MakeSearchRequest
 */
export class SearchFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SearchFailure;

  /**
   * Contains error message
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Issued when detailed search is successful
 *
 * @see MakeDetailedSearchRequest
 */
export class DetailedSearchSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.DetailedSearchSuccess;

  /**
   * Contains result set
   *
   * @param payload {DetailedResult}
   */
  constructor(public payload: DetailedResult) {}
}

/**
 * Issued when detailed save is successful
 *
 * @see MakeSaveDetailedEditRequest
 */
export class DetailedSaveSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.DetailedSaveSuccess;

  /**
   * Contains result set
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Issued when detailed create is successful
 *
 * @see MakeDetailedNewRequest
 */
export class DetailedNewSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.DetailedNewSuccess;

  /**
   * Contains result set
   *
   * @param payload {Object}
   */
  constructor(public payload: any) {}
}

/**
 * Issued when detailed search has failed
 *
 * @see MakeDetailedSearchRequest
 */
export class DetailedSearchFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.DetailedSearchFailure;

  /**
   * Contains error message
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Issued when detailed save has failed
 *
 * @see MakeSaveDetailedEditRequest
 */
export class DetailedSaveFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.DetailedSaveFailure;

  /**
   * Contains error message
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Issued when basket search is successful
 *
 * @see MakeBasketSearchRequest
 */
export class BasketSearchSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.BasketSearchSuccess;

  /**
   * Contains result set
   *
   * @param payload {Object}
   */
  constructor(public payload: any) {}
}

/**
 * Issued when basket search has failed
 *
 * @see MakeBasketSearchRequest
 */
export class BasketSearchFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.BasketSearchFailure;

  /**
   * Contains error message
   *
   * @param payload
   */
  constructor(public payload: string) {}
}

/**
 * Load page wise next simple search results of current query and add them to the current results.
 */
export class NextPage implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.NextPage;

  /**
   * Contains success message
   *
   * @param payload the last doc's id
   */
  constructor(public payload: any) {}
}

/**
 * Loading of next simple search results succeeded.
 */
export class NextPageSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.NextPageSuccess;

  /**
   * Contains success message
   *
   * @param payload the received search results
   */
  constructor(public payload: Result[]) {}
}

/**
 * Perform a simple text search.
 */
export class SimpleSearch implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SimpleSearch;

  /**
   * Contains extra request params
   *
   * @param payload the extra request params
   */
  constructor(public payload?: Partial<QueryFormat>) {}
}

/**
 * Perform a simple text search.
 */
export class SuggestSearchWordSearch implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SuggestSearchWordSearch;

  /**
   * Contains extra request params
   *
   * @param payload the extra request params
   */
  constructor(public payload: { text: string; selected: AutoCompleteValue[] }) {}
}

/**
 * Issued when suggest word search is successful
 *
 * @see MakeSearchRequest
 */
export class SuggestSearchWordSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SuggestSearchWordSuccess;

  /**
   * Contains result set
   *
   * @param payload {AutoCompleteValue[]}
   */
  constructor(public payload: AutoCompleteValue[]) {}
}

/**
 * Issued when suggest word search has failed
 *
 * @see MakeSearchRequest
 */
export class SuggestSearchWordFailure implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.SuggestSearchWordFailure;

  /**
   * Contains error message
   *
   * @param payload {string}
   */
  constructor(public payload: string) {}
}

/**
 * Reset search params except rows per page and sort field/order.
 */
export class Reset implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.Reset;
}

/**
 * Reset search params: rows per page and sort field/order.
 */
export class ResetQuery implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.ResetQuery;
}

/**
 * Perform an in-facet text search.
 */
export class InFacetSearch implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.InFacetSearch;
}

/**
 * Issued when in-facet search is successful
 *
 * @see MakeInFacetSearchRequest
 */
export class InFacetSearchSuccess implements Action {
  /**
   * @ignore
   */
  readonly type = QueryActionTypes.InFacetSearchSuccess;

  /**
   * Contains result set
   *
   * @param payload {FacetResponseMap}
   */
  constructor(public payload: FacetResponseMap) {}
}

/**
 * @ignore
 */
export type QueryActions =
  | MakeSearchRequest
  | MakeGeoSearchRequest
  | MakeBasketSearchRequest
  | MakeInFacetSearchRequest
  | MakeNextDetailedSearchRequest
  | MakePreviousDetailedSearchRequest
  | SetOffset
  | SetGeoClusterPrecision
  | SetRows
  | SetSortField
  | SetSortOrder
  | SetResultListDisplayMode
  | SearchSuccess
  | SearchFailure
  | BasketSearchSuccess
  | BasketSearchFailure
  | NextPage
  | NextPageSuccess
  | SimpleSearch
  | Reset
  | InFacetSearch
  | InFacetSearchSuccess
  | ResetQuery;
